import { T } from "../lib/fc3i18n";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapPin, LocateFixed, Search, Navigation, Phone, Globe, Clock, Loader2, ShieldCheck } from "lucide-react";

/* ------------------------------------------------------------------ *
 * Women-friendly gym, yoga & dance studio finder.
 * Data: OpenStreetMap (Overpass API) · Geocoding: Nominatim.
 * Location is used only in the browser to search; FemCare does not
 * store it. Map: Leaflet (loaded on demand) with OSM tiles.
 * ------------------------------------------------------------------ */

const OVERPASS = "https://overpass-api.de/api/interpreter";
const NOMINATIM = "https://nominatim.openstreetmap.org/search";
const LEAFLET_VER = "1.9.4";
let leafletPromise = null;
function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (leafletPromise) return leafletPromise;
  leafletPromise = new Promise((resolve, reject) => {
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = `https://unpkg.com/leaflet@${LEAFLET_VER}/dist/leaflet.css`;
    document.head.appendChild(css);
    const js = document.createElement("script");
    js.src = `https://unpkg.com/leaflet@${LEAFLET_VER}/dist/leaflet.js`;
    js.async = true;
    js.onload = () => resolve(window.L);
    js.onerror = () => {
      leafletPromise = null;
      reject(new Error("map failed to load"));
    };
    document.body.appendChild(js);
  });
  return leafletPromise;
}
const TYPES = [{
  key: "all",
  label: "All"
}, {
  key: "women",
  label: "Women-only"
}, {
  key: "gym",
  label: "Gyms"
}, {
  key: "yoga",
  label: "Yoga"
}, {
  key: "dance",
  label: "Dance & Zumba"
}];
const WOMEN_RE = /\b(ladies|lady|women|woman|womens|female|girls|mahila|she|her)\b/i;
function classify(tags) {
  const name = tags.name || "";
  const sport = (tags.sport || "").toLowerCase();
  const women = ["only", "yes"].includes((tags.female || "").toLowerCase()) && (tags.male || "").toLowerCase() === "no" || (tags.female || "").toLowerCase() === "only" || (tags.gender || "").toLowerCase() === "female" || WOMEN_RE.test(name);
  const yoga = sport.includes("yoga") || /yoga/i.test(name);
  const dance = sport.includes("dance") || sport.includes("aerobics") || /zumba|dance|aerobic/i.test(name) || tags.leisure === "dance";
  return {
    women,
    yoga,
    dance,
    gym: !yoga && !dance
  };
}
function distKm(a, b) {
  const R = 6371;
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLon = (b.lon - a.lon) * Math.PI / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}
function addressOf(t) {
  const parts = [t["addr:housenumber"], t["addr:street"], t["addr:suburb"] || t["addr:neighbourhood"], t["addr:city"]].filter(Boolean);
  return parts.join(", ");
}
export default function GymFinder() {
  const [center, setCenter] = useState(null); // {lat, lon, label}
  const [radius, setRadius] = useState(5);
  const [query, setQuery] = useState("");
  const [type, setType] = useState("all");
  const [places, setPlaces] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | locating | loading | done | error
  const [msg, setMsg] = useState("");
  const [activeId, setActiveId] = useState(null);
  const mapEl = useRef(null);
  const map = useRef(null);
  const layer = useRef(null);
  const locate = () => {
    if (!("geolocation" in navigator)) {
      setStatus("error");
      setMsg("Your browser can't share location. Search for your area instead.");
      return;
    }
    setStatus("locating");
    setMsg("");
    navigator.geolocation.getCurrentPosition(p => setCenter({
      lat: p.coords.latitude,
      lon: p.coords.longitude,
      label: "Your location"
    }), () => {
      setStatus("error");
      setMsg("Location permission was not given. Type your area or city instead.");
    }, {
      enableHighAccuracy: false,
      timeout: 12000,
      maximumAge: 300000
    });
  };
  const searchArea = async e => {
    e.preventDefault();
    if (!query.trim()) return;
    setStatus("loading");
    setMsg("");
    try {
      const r = await fetch(`${NOMINATIM}?format=json&limit=1&q=${encodeURIComponent(query.trim())}`, {
        headers: {
          Accept: "application/json"
        }
      });
      const j = await r.json();
      if (!j.length) {
        setStatus("error");
        setMsg("We couldn't find that place. Try a nearby area or city name.");
        return;
      }
      setCenter({
        lat: parseFloat(j[0].lat),
        lon: parseFloat(j[0].lon),
        label: j[0].display_name.split(",").slice(0, 2).join(",")
      });
    } catch {
      setStatus("error");
      setMsg("Search is busy right now. Please try again in a moment.");
    }
  };

  // fetch places when center/radius change
  useEffect(() => {
    if (!center) return;
    let cancelled = false;
    (async () => {
      setStatus("loading");
      const r = Math.round(radius * 1000);
      const a = `(around:${r},${center.lat},${center.lon})`;
      const q = `[out:json][timeout:25];(
        nwr["leisure"="fitness_centre"]${a};
        nwr["leisure"="sports_centre"]["sport"~"fitness|yoga|aerobics|dance|gymnastics"]${a};
        nwr["sport"="yoga"]${a};
        nwr["amenity"="studio"]["studio"~"dance|yoga"]${a};
        nwr["leisure"="dance"]${a};
      );out center tags 120;`;
      try {
        const res = await fetch(OVERPASS, {
          method: "POST",
          body: "data=" + encodeURIComponent(q),
          headers: {
            "Content-Type": "application/x-www-form-urlencoded"
          }
        });
        if (!res.ok) throw new Error("overpass " + res.status);
        const j = await res.json();
        if (cancelled) return;
        const seen = new Set();
        const list = (j.elements || []).map(el => {
          const lat = el.lat ?? el.center?.lat;
          const lon = el.lon ?? el.center?.lon;
          const t = el.tags || {};
          if (lat == null || !t.name) return null;
          const key = t.name.toLowerCase() + Math.round(lat * 1000) + Math.round(lon * 1000);
          if (seen.has(key)) return null;
          seen.add(key);
          return {
            id: `${el.type}/${el.id}`,
            lat,
            lon,
            name: t.name,
            tags: t,
            kind: classify(t),
            km: distKm(center, {
              lat,
              lon
            })
          };
        }).filter(Boolean).sort((x, y) => x.km - y.km);
        setPlaces(list);
        setStatus("done");
        setMsg(list.length ? "" : "No listed gyms or studios found here yet. Try a bigger radius.");
      } catch {
        if (!cancelled) {
          setStatus("error");
          setMsg("The map data service is busy. Please try again in a minute.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [center, radius]);
  const filtered = useMemo(() => places.filter(p => type === "all" || p.kind[type]), [places, type]);

  // map rendering
  useEffect(() => {
    if (!center || !mapEl.current) return;
    let alive = true;
    loadLeaflet().then(L => {
      if (!alive) return;
      if (!map.current) {
        map.current = L.map(mapEl.current, {
          scrollWheelZoom: false
        }).setView([center.lat, center.lon], 13);
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        }).addTo(map.current);
      }
      if (layer.current) layer.current.remove();
      layer.current = L.layerGroup().addTo(map.current);
      L.circleMarker([center.lat, center.lon], {
        radius: 9,
        color: "#fff",
        weight: 3,
        fillColor: "#6B5BEF",
        fillOpacity: 1
      }).bindTooltip(center.label).addTo(layer.current);
      L.circle([center.lat, center.lon], {
        radius: radius * 1000,
        color: "#6B5BEF",
        weight: 1,
        fillOpacity: 0.04
      }).addTo(layer.current);
      filtered.forEach(p => {
        const c = p.kind.women ? "#E0306F" : p.kind.yoga ? "#6B5BEF" : p.kind.dance ? "#F29A38" : "#13896F";
        L.circleMarker([p.lat, p.lon], {
          radius: p.id === activeId ? 11 : 8,
          color: "#fff",
          weight: 2,
          fillColor: c,
          fillOpacity: 0.95
        }).bindPopup(`<strong>${p.name.replace(/</g, "&lt;")}</strong><br/>${p.km.toFixed(1)} km away`).on("click", () => setActiveId(p.id)).addTo(layer.current);
      });
      map.current.setView([center.lat, center.lon], radius <= 2 ? 15 : radius <= 5 ? 13 : 12);
      setTimeout(() => map.current && map.current.invalidateSize(), 50);
    }).catch(() => setMsg("The map couldn't load, but the list below still works."));
    return () => {
      alive = false;
    };
  }, [center, filtered, radius, activeId]);
  useEffect(() => () => {
    if (map.current) {
      map.current.remove();
      map.current = null;
    }
  }, []);
  const focus = p => {
    setActiveId(p.id);
    if (map.current) map.current.setView([p.lat, p.lon], 16);
  };
  return <div className="rounded-[32px] bg-white border border-[#F1E6EA] shadow-[0_40px_80px_-50px_rgba(43,33,64,0.4)] overflow-hidden" data-testid="gym-finder">
      <div className="p-5 md:p-7 border-b border-[#F3EEF6] flex flex-col lg:flex-row gap-3 lg:items-center">
        <button type="button" onClick={locate} className="fc-btn-violet inline-flex items-center justify-center gap-2 min-h-[52px] rounded-full px-6 font-extrabold shrink-0" data-testid="gym-use-location">
          {status === "locating" ? <Loader2 className="w-5 h-5 animate-spin" /> : <LocateFixed className="w-5 h-5" />} {T("Use my location")}</button>
        <span className="text-center text-[14px] font-bold text-[#9A94A8]">{T("or")}</span>
        <form onSubmit={searchArea} className="flex-1 flex gap-2">
          <label htmlFor="gym-q" className="sr-only">{T("Area or city")}</label>
          <div className="flex-1 relative">
            <MapPin className="w-5 h-5 text-[#9A94A8] absolute left-4 top-1/2 -translate-y-1/2" aria-hidden="true" />
            <input id="gym-q" value={query} onChange={e => setQuery(e.target.value)} placeholder={T("Area or city, e.g. Koramangala, Bengaluru")} className="w-full min-h-[52px] rounded-full border border-[#E9DCE3] bg-[#FFF8F9] pl-12 pr-4 text-[16px] focus:outline-none focus:border-[#6B5BEF]" />
          </div>
          <button type="submit" className="min-h-[52px] min-w-[52px] rounded-full bg-[#1E1A2B] text-white flex items-center justify-center" aria-label={T("Search area")}><Search className="w-5 h-5" /></button>
        </form>
        <label className="flex items-center gap-2 text-[14px] font-bold text-[#2B2140] shrink-0">{T("Within")} <select value={radius} onChange={e => setRadius(Number(e.target.value))} className="min-h-[44px] rounded-full border border-[#E9DCE3] bg-white px-3 font-bold">
            {[2, 5, 10, 20].map(k => <option key={k} value={k}>{k} {T("km")}</option>)}
          </select>
        </label>
      </div>

      {!center ? <div className="p-8 md:p-12 text-center">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-[#EFEBFF] text-[#5A49E0] flex items-center justify-center"><MapPin className="w-8 h-8" /></div>
          <p className="mt-4 text-[20px] font-extrabold text-[#1E1A2B]">{T("Find gyms, yoga and dance studios near you")}</p>
          <p className="mt-2 text-[15px] text-[#5F5A70] max-w-lg mx-auto">{T("Your location is only used in your browser to search the map. FemCare does not save it.")}</p>
          {msg && <p className="mt-4 text-[15px] font-semibold text-[#D02868]" role="alert">{msg}</p>}
        </div> : <div className="grid lg:grid-cols-12">
          <div className="lg:col-span-7 relative">
            <div ref={mapEl} className="h-[340px] lg:h-[560px] w-full bg-[#F3EEF6]" aria-label={T("Map of nearby places")} />
          </div>
          <div className="lg:col-span-5 flex flex-col max-h-[560px]">
            <div className="px-5 pt-4 pb-3 flex flex-wrap gap-2 border-b border-[#F3EEF6]" role="tablist" aria-label={T("Filter places")}>
              {TYPES.map(t => <button key={t.key} type="button" role="tab" aria-selected={type === t.key} onClick={() => setType(t.key)} className={`min-h-[40px] rounded-full px-4 text-[14px] font-bold border transition-colors ${type === t.key ? "bg-[#1E1A2B] text-white border-[#1E1A2B]" : "bg-white text-[#2B2140] border-[#E9DCE3]"}`}>
                  {T(t.label)}
                </button>)}
            </div>
            <div className="flex-1 overflow-y-auto" aria-live="polite">
              {status === "loading" && <p className="p-6 flex items-center gap-2 text-[15px] text-[#5F5A70]"><Loader2 className="w-5 h-5 animate-spin" /> {T("Searching near {{place}}…", { place: T(center.label) })}</p>}
              {status !== "loading" && msg && <p className="p-6 text-[15px] font-semibold text-[#D02868]" role="alert">{msg}</p>}
              {status === "done" && <>
                  <p className="px-5 pt-4 text-[13px] font-bold text-[#7A7488]">{T("{{n}} place(s) within {{r}} km of {{place}}", { n: filtered.length, r: radius, place: T(center.label) })}</p>
                  {type === "women" && filtered.length === 0 && places.length > 0 && <p className="px-5 pt-2 text-[14px] text-[#5F5A70]">{T("No places here are marked women-only on OpenStreetMap yet. Many gyms offer ladies' batches — call to ask.")}</p>}
                  <ul className="p-3 space-y-2">
                    {filtered.map(p => {
                const t = p.tags;
                const addr = addressOf(t);
                return <li key={p.id}>
                          <div className={`rounded-2xl p-4 border transition-colors cursor-pointer ${activeId === p.id ? "border-[#6B5BEF] bg-[#F6F3FF]" : "border-[#F1E6EA] hover:border-[#D9CDE6]"}`} onClick={() => focus(p)} onKeyDown={e => {
                    if (e.key === "Enter") focus(p);
                  }} tabIndex={0} role="button" aria-label={`Show ${p.name} on map`}>
                            <div className="flex items-start justify-between gap-3">
                              <p className="text-[16px] font-extrabold text-[#1E1A2B] leading-snug">{T(p.name)}</p>
                              <span className="shrink-0 text-[13px] font-bold text-[#5F5A70]">{p.km.toFixed(1)} {T("km")}</span>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {p.kind.women && <span className="inline-flex items-center gap-1 rounded-full bg-[#FFE9EF] text-[#D02868] px-2.5 py-0.5 text-[12px] font-bold"><ShieldCheck className="w-3.5 h-3.5" />{T("Women-only")}</span>}
                              {p.kind.yoga && <span className="rounded-full bg-[#EFEBFF] text-[#5A49E0] px-2.5 py-0.5 text-[12px] font-bold">{T("Yoga")}</span>}
                              {p.kind.dance && <span className="rounded-full bg-[#FFF1DC] text-[#B4630E] px-2.5 py-0.5 text-[12px] font-bold">{T("Dance")}</span>}
                              {p.kind.gym && <span className="rounded-full bg-[#E3F8F2] text-[#13896F] px-2.5 py-0.5 text-[12px] font-bold">{T("Gym")}</span>}
                            </div>
                            {addr && <p className="mt-2 text-[14px] text-[#5F5A70]">{addr}</p>}
                            {t.opening_hours && <p className="mt-1 text-[13px] text-[#5F5A70] flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{t.opening_hours}</p>}
                            <div className="mt-3 flex flex-wrap gap-2">
                              <a href={`https://www.openstreetmap.org/directions?route=${center.lat}%2C${center.lon}%3B${p.lat}%2C${p.lon}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1.5 min-h-[40px] rounded-full bg-[#1E1A2B] text-white px-4 text-[13px] font-bold">
                                <Navigation className="w-4 h-4" /> {T("Directions")}</a>
                              {(t.phone || t["contact:phone"]) && <a href={`tel:${(t.phone || t["contact:phone"]).split(";")[0].replace(/\s/g, "")}`} onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1.5 min-h-[40px] rounded-full border border-[#E9DCE3] px-4 text-[13px] font-bold text-[#2B2140]">
                                  <Phone className="w-4 h-4" /> {T("Call")}</a>}
                              {(t.website || t["contact:website"]) && <a href={t.website || t["contact:website"]} target="_blank" rel="noopener noreferrer nofollow" onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1.5 min-h-[40px] rounded-full border border-[#E9DCE3] px-4 text-[13px] font-bold text-[#2B2140]">
                                  <Globe className="w-4 h-4" /> {T("Website")}</a>}
                            </div>
                          </div>
                        </li>;
              })}
                  </ul>
                </>}
            </div>
          </div>
        </div>}
      <p className="px-5 md:px-7 py-4 text-[12px] text-[#7A7488] border-t border-[#F3EEF6]">{T("Listings come from")} <a className="underline" href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">{T("OpenStreetMap")}</a> {T("contributors and may be incomplete or out of date. \"Women-only\" is shown only where the place is tagged or named that way — please confirm with the gym. FemCare does not endorse listed businesses.")}</p>
    </div>;
}